import {
  FacilityListParams,
  FacilityOrder,
  FacilitySort,
  Gender,
  Prefecture,
  PrefectureDetail,
  ProgramLevel,
} from '@gyym/sdk'
import { ParsedUrlQuery } from 'querystring'
import * as z from 'zod'

import {
  toBoolean,
  toNumber,
  toNumberArray,
  toStringArray,
  toZod,
} from '@/lib/zod'
import { getActivitySlugById } from '@/modules/Activities'
import { formatCityCode } from '@/modules/Facilities/utils/buildFacilityLink'
import { getPrefectureSlugById } from '@/modules/Prefectures'

import {
  AREA_ID_PREFIX,
  castSegmentValue,
  CITY_CODE_PREFIX,
  getSegmentType,
  SegmentType,
} from './searchSegments'

const searchQuerySchema = z.object<toZod<FacilityListParams>>({
  facilityName: z.string().nullable().optional(),
  prefectureIds: z.preprocess(
    toNumberArray,
    z.array(z.nativeEnum(Prefecture)).optional(),
  ),
  areaIds: z.preprocess(toNumberArray, z.array(z.number()).optional()),
  dates: z.preprocess(toStringArray, z.array(z.string()).optional()),
  startTimeFrom: z.string().nullable().optional(),
  startTimeTo: z.string().nullable().optional(),
  activityIds: z.preprocess(toNumberArray, z.array(z.number()).optional()),
  tags: z.preprocess(toStringArray, z.array(z.string()).optional()),
  priceFrom: z.preprocess(toNumber, z.number().nullable().optional()),
  priceTo: z.preprocess(toNumber, z.number().nullable().optional()),
  sexLimitations: z.preprocess(
    toStringArray,
    z.array(z.nativeEnum(Gender)).optional(),
  ),
  exerciseLevels: z.preprocess(
    toNumberArray,
    z.array(z.nativeEnum(ProgramLevel)).optional(),
  ),
  excludeFullReservations: z.preprocess(toBoolean, z.boolean().optional()),
  omitPrograms: z.preprocess(toBoolean, z.boolean().optional()),
  page: z.preprocess(toNumber, z.number().optional()),
  numPerPage: z.preprocess(toNumber, z.number().optional()),
  sortBy: z.nativeEnum(FacilitySort).optional(),
  order: z.nativeEnum(FacilityOrder).optional(),
  cityCode: z.string().optional(),
})

/**
 * Convert query to search params
 *
 * @param q URL Query
 * @returns FacilityListParams
 */
export const fromQuery = (q: ParsedUrlQuery): FacilityListParams => {
  return searchQuerySchema.parse(q)
}

/**
 * Return URLSearchParams from search params object
 * @param params
 * @returns URLSearchParams
 */
export const toSearchParams = (params: FacilityListParams): URLSearchParams => {
  const values = searchQuerySchema.parse(params)
  const q = new URLSearchParams()

  Object.entries(values).forEach(([key, value]) => {
    if (!value) return

    if (Array.isArray(value)) {
      value.forEach((v) => q.append(key, v.toString()))
      return
    }

    q.set(key, value.toString())
  })

  return q
}

/**
 * Convert search params to URL query
 *
 * @param params FacilityListParams
 * @returns string
 */
export const toQuery = (params: FacilityListParams): string => {
  const qs = toSearchParams(params).toString()
  return qs ? `?${qs}` : ''
}

/**
 * Convert URLSearchParams to object with string or string[] values
 * @param searchParams URLSearchParams
 * @returns object
 */
export const toQueryObject = (
  searchParams: URLSearchParams,
): { [key: string]: string | string[] } =>
  Array.from(searchParams.keys()).reduce((acc, key) => {
    const allValue = searchParams.getAll(key)

    if (allValue.length > 1) {
      return {
        ...acc,
        [key]: allValue,
      }
    }

    const value = searchParams.get(key)
    if (!value) return acc

    return {
      ...acc,
      [key]: value,
    }
  }, {} as { [key: string]: string | string[] })

type HrefType = {
  pathname: string
  query: { [key: string]: string | string[] }
}

/**
 * Get link props from params
 */
export const toLink = (
  params: FacilityListParams,
  prefectures: PrefectureDetail[],
  {
    isMapView,
  }: {
    isMapView?: boolean
  } = {},
): {
  as: string
  href: HrefType
} => {
  const basePath = '/facilities'

  const segments = []
  const pathInfo = {
    pathname: basePath,
    query: {},
  } as HrefType

  const {
    prefectureIds = [],
    areaIds = [],
    activityIds = [],
    tags = [],
    cityCode,
    ...rest
  } = params

  // `/facilities/:tag`
  const isTagSearch = tags.length === 1

  if (isTagSearch) {
    const tag = tags[0]
    segments.push(tag)
    pathInfo.pathname = `${pathInfo.pathname}/tags/[tag]`
    pathInfo.query = { ...pathInfo.query, tag: tag ?? '' }
  }

  // `/facilities/:prefectureSlug`
  const isPrefectureSearch =
    prefectureIds.length === 1 && areaIds.length === 0 && !cityCode

  if (isPrefectureSearch) {
    const prefectureSlug = getPrefectureSlugById(prefectureIds[0]!)

    if (prefectureSlug) {
      segments.push(prefectureSlug)
      pathInfo.pathname = `${pathInfo.pathname}/prefectures/[prefectureSlug]`
      pathInfo.query = { ...pathInfo.query, prefectureSlug }
    }
  }

  // `/facilities/:prefectureSlug/:areaId`
  const isAreaSearch =
    segments.length === 0 && prefectureIds.length === 0 && areaIds.length === 1

  if (isAreaSearch) {
    // Find prefecture by area
    const prefecture = prefectures.find((p) =>
      p.areas.some((a) => a.id === areaIds[0]),
    )

    if (!prefecture) {
      throw new Error(`Prefecture not found by area id: ${areaIds[0]}`)
    }

    const prefectureSlug = getPrefectureSlugById(prefecture.id)

    if (prefectureSlug) {
      segments.push(prefectureSlug, `${AREA_ID_PREFIX}${areaIds[0]}`)
      pathInfo.pathname = `${pathInfo.pathname}/prefectures/[prefectureSlug]/areas/[areaId]`
      pathInfo.query = {
        ...pathInfo.query,
        prefectureSlug,
        areaId: areaIds[0]!.toString(),
      }
    }
  }

  // `/facilities/:prefectureSlug/:cityCode`
  const isCityCodeSearch =
    segments.length === 0 && prefectureIds.length === 1 && cityCode

  if (isCityCodeSearch) {
    // Find prefecture by city code
    const prefecture = prefectures.find((p) =>
      p.cities.some((c) => c.code === Number(cityCode)),
    )

    if (!prefecture) {
      throw new Error(`Prefecture not found by city code: ${cityCode}`)
    }

    const prefectureSlug = getPrefectureSlugById(prefecture.id)

    if (prefectureSlug) {
      // City codes of prefectures 01-09 lose their leading 0 as numbers
      const urlCityCode = formatCityCode(cityCode)

      segments.push(prefectureSlug, `${CITY_CODE_PREFIX}${urlCityCode}`)
      pathInfo.pathname = `${pathInfo.pathname}/prefectures/[prefectureSlug]/cities/[cityCode]`
      pathInfo.query = {
        ...pathInfo.query,
        prefectureSlug,
        cityCode: urlCityCode,
      }
    }
  }

  const shouldAppendActivity = activityIds.length === 1

  if (shouldAppendActivity) {
    const activitySlug = getActivitySlugById(activityIds[0]!)

    if (activitySlug) {
      segments.push(activitySlug)
      pathInfo.pathname = `${pathInfo.pathname}/activities/[activitySlug]`
      pathInfo.query = { ...pathInfo.query, activitySlug }
    }
  }

  const searchParams = toSearchParams({
    ...rest,
    prefectureIds:
      !isPrefectureSearch && !isCityCodeSearch ? prefectureIds : undefined,
    areaIds: !isAreaSearch ? areaIds : undefined,
    activityIds: !shouldAppendActivity ? activityIds : undefined,

    // Never pass tags/cityCode to query (Should only exist in path)
    tags: undefined,
    cityCode: undefined,
  })

  const query = searchParams.toString()

  const qs = `${query ? `?${query}` : ''}${
    isMapView ? (query ? '&view=map' : '?view=map') : ''
  }`

  const as = `${basePath}${segments.length ? '/' : ''}${segments.join(
    '/',
  )}${qs}`

  return {
    as,
    href: {
      pathname: pathInfo.pathname,
      query: {
        ...toQueryObject(searchParams),
        ...pathInfo.query,
        ...(isMapView ? { view: 'map' } : {}),
      },
    },
  }
}

/**
 * Convert params to path
 *
 * @example /facilities/tokyo
 * @example /facilities/saitama/C11107
 * @example /facilities/tokyo/A1
 * @example /facilities/yoga
 * @example /facilities/tokyo/yoga
 * @example /facilities/saitama/C11107/yoga
 * @example /facilities/tokyo/A1/yoga
 */
export const toPath = (
  params: FacilityListParams,
  prefectures: PrefectureDetail[],
  {
    isMapView,
  }: {
    isMapView?: boolean
  } = {},
): string => toLink(params, prefectures, { isMapView }).as

/**
 * Get search query from segment value
 *
 * @param value Segment value
 * @returns Partial<FacilityListParams>
 */
const getSegmentSearchQuery = (value: string): Partial<FacilityListParams> => {
  const type = getSegmentType(value)

  switch (type) {
    case SegmentType.PREFECTURE:
      return { prefectureIds: [castSegmentValue(type, value)] }
    case SegmentType.ACTIVITY:
      return { activityIds: [castSegmentValue(type, value)] }
    case SegmentType.AREA_ID:
      return { areaIds: [castSegmentValue(type, value)] }
    case SegmentType.CITY_CODE:
      return { cityCode: castSegmentValue(type, value) }
    case SegmentType.TAG:
      return { tags: [castSegmentValue(type, value)] }
    default:
      return {}
  }
}

/**
 * Convert path segments to search query
 *
 * @param segments Array of path segments
 * @returns Partial<FacilityListParams>
 */
export const fromPathSegments = ([first, second, third]:
  | []
  | [string]
  | [string, string]
  | [string, string, string]): Partial<FacilityListParams> => {
  const firstSegmentQuery = first ? getSegmentSearchQuery(first) : {}
  const secondSegmentQuery = second ? getSegmentSearchQuery(second) : {}
  const thirdSegmentQuery = third ? getSegmentSearchQuery(third) : {}

  // Return only areaIds if prefectureIds and areaIds are both specified
  if (firstSegmentQuery.prefectureIds && secondSegmentQuery.areaIds) {
    return {
      ...secondSegmentQuery,
      ...thirdSegmentQuery,
    }
  }

  return {
    ...firstSegmentQuery,
    ...secondSegmentQuery,
    ...thirdSegmentQuery,
  }
}
